import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useExportFormat,
  useUpdateExportFormat,
  useDeleteExportFormat,
  type ExportFormat,
} from "../hooks/useExportFormats";
import { FormatSettingsForm } from "../export-formats/components/FormatSettingsForm";
import { FormatColumnsEditor, type FormatColumn } from "../export-formats/components/FormatColumnsEditor";
import { FormatStructureEditor } from "../export-formats/components/FormatStructureEditor.js";
import { FormatLayoutPreview } from "../export-formats/components/FormatLayoutPreview";
import { ConformancePanel } from "../export-formats/components/ConformancePanel";
import {
  PartnerProfileForm,
  partnerFieldToText,
  orNull,
  type PartnerProfileValue,
} from "../export-formats/components/PartnerProfileForm";
import { type SchemaImportResult } from "../export-formats/components/SchemaImportModal";
import { toast } from "@/components/Toaster";
import { ConfirmModal } from "@/components/ConfirmModal";
import {
  normalizeColumnFormat,
  flatColumnsToStructure,
  flattenStructureLeaves,
  deriveColumnsFromStructure,
  validateFormatStructure,
  type FormatType,
  type ConformanceContract,
  type ExportFormatStructureNode,
} from "@capture/shared";
import "../admin.css";

export function ExportFormatDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { format, isLoading, refetch } = useExportFormat(id);
  const { update, patch, isSubmitting } = useUpdateExportFormat(id ?? "");
  const { remove } = useDeleteExportFormat();

  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Edit-mode state
  const [editing, setEditing] = useState(false);
  const [draftSettings, setDraftSettings] = useState<Record<string, unknown>>({});
  const [draftColumns, setDraftColumns] = useState<FormatColumn[]>([]);
  const [draftStructure, setDraftStructure] = useState<ExportFormatStructureNode[]>([]);
  const [draftContract, setDraftContract] = useState<ConformanceContract | null>(null);
  const [draftProfile, setDraftProfile] = useState<PartnerProfileValue>({
    fileNamingPattern: "",
    partnerNotes: "",
    schemaAttachment: "",
    sampleData: "",
  });

  useEffect(() => {
    if (format) setName(format.name);
  }, [format]);

  const enterEditMode = () => {
    if (!format) return;
    setDraftSettings({ ...(format.config ?? {}) });
    const flagByName = new Map<string, { required?: boolean; allowEmpty?: boolean }>();
    for (const leaf of flattenStructureLeaves((format.structure as ExportFormatStructureNode[]) ?? [])) {
      flagByName.set(leaf.node.name, { required: leaf.node.required, allowEmpty: leaf.node.allowEmpty });
    }
    setDraftColumns((format.columns ?? []).map(c => {
      const format_spec = normalizeColumnFormat(c);
      const flags = flagByName.get(c.name);
      return {
        ...c,
        dataType: format_spec.kind,
        format: format_spec,
        required: flags?.required ?? false,
        allowEmpty: flags?.allowEmpty ?? true,
      };
    }));
    setDraftStructure((format.structure as ExportFormatStructureNode[]) ?? []);
    setDraftProfile({
      fileNamingPattern: partnerFieldToText(format.fileNamingPattern),
      partnerNotes: partnerFieldToText(format.partnerNotes),
      schemaAttachment: partnerFieldToText(format.schemaAttachment),
      sampleData: partnerFieldToText(format.sampleData),
    });
    setDraftContract((format.conformanceContract as ConformanceContract | null) ?? null);
    setError(null);
    setEditing(true);
  };

  // FR-001 / Task 5 (Partner Profile Completion) — a schema import carries
  // both the parsed conformance contract (may be null if no policy was
  // switched on) and the raw pasted text, stored as provenance in
  // schemaAttachment.
  const handleImport = (result: SchemaImportResult) => {
    setDraftContract(result.contract);
    setDraftProfile((prev) => ({ ...prev, schemaAttachment: result.rawSchemaText }));
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setError(null);
    try {
      await update({ name: name.trim() });
      toast.success("Format updated");
      navigate("/admin/export-formats");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const handleSaveChanges = async () => {
    setError(null);
    const isTreeFormat = format?.formatType === "xml" || format?.formatType === "json";
    if (isTreeFormat) {
      const v = validateFormatStructure(draftStructure);
      if (v.errors.length > 0) {
        setError(v.errors.map((e) => e.message).join("; "));
        return;
      }
    }
    try {
      // E-015 P2 — empty string clears the field (sent as null); otherwise the
      // raw text is stored verbatim (server accepts strings).
      await patch({
        settings: draftSettings,
        structure: isTreeFormat ? draftStructure : flatColumnsToStructure(draftColumns),
        fileNamingPattern: orNull(draftProfile.fileNamingPattern),
        partnerNotes: orNull(draftProfile.partnerNotes),
        schemaAttachment: orNull(draftProfile.schemaAttachment),
        sampleData: orNull(draftProfile.sampleData),
        conformanceContract: draftContract,
      });
      // F-030 — refresh so the saved columns render in place; without this the
      // page kept showing the pre-edit column set until a hard refresh, which
      // read as data loss.
      await refetch();
      toast.success("Format updated");
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDelete = () => {
    if (!id) return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!id) return;
    setConfirmDeleteOpen(false);
    try {
      await remove(id);
      navigate("/admin/export-formats");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to delete";
      toast.error(message);
    }
  };

  if (isLoading || !format) {
    return (
      <div className="admin-page">
        <div className="admin-loading"><div className="loading-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="admin-back" onClick={() => navigate("/admin/export-formats")}>
          &larr; Export Formats
        </button>
        <h1 className="admin-title">{format.name}</h1>
      </div>

      <div className="admin-form-content">
        {/* R-186 — explain the format vs mapping split + link to the mapping layer. */}
        <div className="admin-help" data-testid="export-format-help" style={{ marginBottom: "var(--space-4)" }}>
          This defines the output <strong>format</strong> — file shape, settings,
          and column structure — reusable across Job Types. The per-Job-Type
          field <strong>mapping</strong> (which field fills which column) lives on
          each Job Type's Form Config → Export tab.
        </div>

        {!editing && (format.destinations?.length ?? 0) === 0 && (
          <div className="tab-placeholder" style={{ marginBottom: "var(--space-4)", flexDirection: "column", alignItems: "flex-start" }}>
            <span className="tab-placeholder-text">
              No Job Type maps data into these columns yet.
            </span>
            <button
              className="rb-btn rb-btn-save"
              style={{ marginTop: "var(--space-3)" }}
              onClick={() => navigate("/admin/job-types")}
            >
              Go to a Job Type to wire data into these columns →
            </button>
          </div>
        )}

        <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
          <div className="rb-row">
            <label className="rb-label" htmlFor="format-name">Name</label>
            <input
              id="format-name"
              className="rb-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="rb-row">
            <span className="rb-label">Format type</span>
            <span className="jt-badge">{format.formatType.replace("_", " ")}</span>
          </div>

          <div className="rb-row">
            <span className="rb-label">Updated</span>
            <span className="rb-muted">{new Date(format.updatedAt).toLocaleString()}</span>
          </div>

          {error && <div className="admin-error" style={{ marginTop: "var(--space-3)" }}>{error}</div>}
        </section>

        {!editing ? (
          <>
            <ColumnsSection format={format} />
            <PartnerProfileSection format={format} />

            <div className="rb-row" style={{ justifyContent: "flex-end", gap: "var(--space-2)" }}>
              <button className="rb-btn rb-btn-danger" onClick={handleDelete}>
                Delete
              </button>
              <button
                className="rb-btn"
                onClick={enterEditMode}
              >
                Edit
              </button>
              <button
                className="rb-btn rb-btn-save"
                onClick={handleSave}
                disabled={isSubmitting || !name.trim()}
              >
                {isSubmitting ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        ) : (
          <>
            <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
              <h2 className="rb-section-title">Settings</h2>
              <FormatSettingsForm
                formatType={format.formatType as FormatType}
                value={draftSettings}
                onChange={setDraftSettings}
              />
            </section>

            <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
              <h2 className="rb-section-title">Columns</h2>
              {(format.formatType === "xml" || format.formatType === "json") ? (
                <>
                  <FormatStructureEditor value={draftStructure} onChange={setDraftStructure} format={format.formatType as "json" | "xml"} onImport={handleImport} />
                  <ConformancePanel columns={deriveColumnsFromStructure(draftStructure) as unknown as FormatColumn[]} contract={draftContract} />
                </>
              ) : (
                <>
                  <FormatColumnsEditor
                    formatType={format.formatType as FormatType}
                    value={draftColumns}
                    onChange={setDraftColumns}
                    onImport={handleImport}
                    transactionGrain={draftSettings.transactionGrain as string | undefined}
                  />
                  <ConformancePanel columns={draftColumns} contract={draftContract} />
                </>
              )}
            </section>

            <FormatLayoutPreview
              formatType={format.formatType}
              config={draftSettings}
              columns={draftColumns}
              sampleData={draftProfile.sampleData}
              structure={
                format.formatType === "xml" || format.formatType === "json"
                  ? draftStructure
                  : flatColumnsToStructure(draftColumns)
              }
            />

            <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
              <h2 className="rb-section-title">Partner Profile</h2>
              <PartnerProfileForm value={draftProfile} onChange={setDraftProfile} />
            </section>

            {error && <div className="admin-error" style={{ marginBottom: "var(--space-3)" }}>{error}</div>}

            <div className="rb-row" style={{ justifyContent: "flex-end", gap: "var(--space-2)" }}>
              <button
                className="rb-btn"
                onClick={cancelEdit}
              >
                Cancel
              </button>
              <button
                className="rb-btn rb-btn-save"
                onClick={handleSaveChanges}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
      <ConfirmModal
        open={confirmDeleteOpen}
        title={`Delete "${format.name}"?`}
        description="This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
}

/**
 * E-015 P2 — read-only Partner Profile display. Shown in the non-edit view so
 * captured metadata is always visible. The whole section is omitted when no
 * profile fields are set, to avoid empty clutter on legacy formats.
 */
function PartnerProfileSection({ format }: { format: ExportFormat }) {
  const naming = partnerFieldToText(format.fileNamingPattern);
  const notes = partnerFieldToText(format.partnerNotes);
  const schema = partnerFieldToText(format.schemaAttachment);
  const sample = partnerFieldToText(format.sampleData);
  const hasAny = naming || notes || schema || sample;

  return (
    <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
      <h2 className="rb-section-title">Partner Profile</h2>
      {!hasAny && (
        <div className="rb-muted">
          No partner profile captured. Use <strong>Edit</strong> to add a file
          naming pattern, partner notes, schema, or sample data.
        </div>
      )}
      {naming && (
        <div className="rb-row">
          <span className="rb-label">File Naming</span>
          <code style={{ fontSize: 13 }}>{naming}</code>
        </div>
      )}
      {naming && (
        <p className="rb-hint" style={{ marginBottom: "var(--space-3)" }}>
          Default for all destinations using this format; a destination's own
          Filename template overrides it.
        </p>
      )}
      {notes && (
        <div className="rb-row" style={{ alignItems: "flex-start" }}>
          <span className="rb-label">Notes</span>
          <span className="rb-muted" style={{ whiteSpace: "pre-wrap" }}>{notes}</span>
        </div>
      )}
      {schema && (
        <div className="rb-row" style={{ alignItems: "flex-start" }}>
          <span className="rb-label">Source schema (provenance)</span>
          <pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", maxHeight: 160, overflow: "auto" }}>{schema}</pre>
        </div>
      )}
      {schema && (
        <p className="rb-hint" style={{ marginBottom: "var(--space-3)" }}>
          The original partner schema this format's conformance contract was
          imported from. Pasting here does NOT enforce anything — use Import
          Partner Schema for enforcement.
        </p>
      )}
      {sample && (
        <div className="rb-row" style={{ alignItems: "flex-start" }}>
          <span className="rb-label">Sample Data</span>
          <pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", maxHeight: 160, overflow: "auto" }}>{sample}</pre>
        </div>
      )}
    </section>
  );
}

// W-002 — surface the typed-column kind in the read view (F-053 types were
// previously only visible in Edit mode). Absent dataType → "Raw" (back-compat).
function typeLabel(dataType?: string): string {
  const t = dataType ?? "raw";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function ColumnsSection({ format }: { format: ExportFormat }) {
  const showPositionLength = format.formatType === "fixed_width";
  // Cast ExportColumn[] to FormatColumn[] for ConformancePanel.
  // Safe because dataType/format are optional in ExportColumn (legacy compatibility),
  // but ConformancePanel only validates against the contract if it exists,
  // and the contract would have required the columns be set up properly.
  const columns = format.columns as unknown as FormatColumn[];

  // Build a name → {required, allowEmpty} map from the persisted structure leaves
  // so the read view can display slot flags without re-deriving them.
  const flagByName = new Map<string, { required: boolean; allowEmpty: boolean }>();
  for (const leaf of flattenStructureLeaves((format.structure as ExportFormatStructureNode[]) ?? [])) {
    flagByName.set(leaf.node.name, {
      required: leaf.node.required ?? false,
      allowEmpty: leaf.node.allowEmpty ?? true,
    });
  }

  return (
    <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)" }}>
        <h2 className="rb-section-title">Columns ({format.columns.length})</h2>
        {format.conformanceContract && (
          <ConformancePanel columns={columns} contract={format.conformanceContract as ConformanceContract} />
        )}
      </div>
      {format.columns.length === 0 && (
        <div className="rb-muted">No columns configured.</div>
      )}
      {format.columns.length > 0 && (
        <table className="rb-table" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Type</th>
              {showPositionLength && <th>Position</th>}
              {showPositionLength && <th>Length</th>}
              <th>Required</th>
              <th>Empty</th>
            </tr>
          </thead>
          <tbody>
            {format.columns.map((col, i) => {
              const flags = flagByName.get(col.name);
              return (
                <tr key={i}>
                  <td>{col.order}</td>
                  <td>{col.name}</td>
                  <td>{typeLabel(col.dataType)}</td>
                  {showPositionLength && <td>{col.position ?? "—"}</td>}
                  {showPositionLength && <td>{col.length ?? "—"}</td>}
                  <td>{(flags?.required ?? false) ? "Yes" : "No"}</td>
                  <td>{(flags?.allowEmpty ?? true) ? "Yes" : "No"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
